import debug from "debug";
import { mount, unmount } from "svelte";
import MyAccountModal from "../../lib/components/modals/MyAccount/MyAccountModal.svelte";

const log = debug("app:myAccountManager");

interface MountedComponent {
  instance: ReturnType<typeof mount>;
  target: HTMLElement;
}

interface EventData {
  event: string;
  [key: string]: unknown;
}

interface AccountUpdatePayload {
  type: "userData" | "password" | "delete";
  data: any;
}

/**
 * Manager for handling account-related events and mounting the MyAccountModal component.
 * Listens to events and manages the account modal lifecycle.
 * @category Components
 * @subcategory SvelteUI
 */
export class MyAccountManager {
  private experience: any;
  private eventHub: any;
  private mountedComponent: MountedComponent | null = null;

  /**
   * Creates an instance of MyAccountManager.
   * @param experience - The experience object containing eventHub and other context.
   */
  constructor(experience: any) {
    this.experience = experience;
    this.eventHub = experience.eventHub;

    this.setupEventListeners();
    log("MyAccountManager initialized");
  }

  /**
   * Sets up event listeners for account-related events.
   */
  private setupEventListeners(): void {
    // Listen for myInfo event to mount MyAccountModal
    this.eventHub.on("myInfo", (data: EventData) => {
      log("Received myInfo event, mounting MyAccountModal", data);
      this.mountMyAccountModal(data);
    });

    // Listen for accountUpdate event to handle account updates
    this.eventHub.on("accountUpdate", (payload: AccountUpdatePayload) => {
      log("Received accountUpdate event", payload);
      this.handleAccountUpdate(payload);
    });

    // Listen for fetchUserRFQs event to fetch user's RFQs
    this.eventHub.on("fetchUserRFQs", async () => {
      log("Received fetchUserRFQs event");
      try {
        const rfqs = await this.fetchUserRFQs();
        this.eventHub.emit("userRFQs:loaded", rfqs);
      } catch (error: any) {
        log("Error in fetchUserRFQs handler:", error);
        this.eventHub.emit("userRFQs:error", error?.message || "Failed to load RFQs");
      }
    });
  }

  /**
   * Mounts the MyAccountModal component.
   * @param data - Data to pass to the component.
   */
  private mountMyAccountModal(data: EventData): void {
    // Unmount existing modal if present
    if (this.mountedComponent) {
      this.unmountMyAccountModal();
    }

    // Mount directly to body - Dialog.Portal will render its content to body anyway
    const container = document.body;

    // Mount the Svelte component using Svelte 5 API
    // The component uses bind:open internally, so we pass open: true to show it initially
    const instance = mount(MyAccountModal, {
      target: container,
      props: {
        open: true,
        experience: this.experience,
        data: data,
      },
    });

    this.mountedComponent = {
      instance,
      target: container,
    };

    log("MyAccountModal mounted");
  }

  /**
   * Unmounts the MyAccountModal component.
   */
  private unmountMyAccountModal(): void {
    if (this.mountedComponent) {
      unmount(this.mountedComponent.instance);
      // Don't try to remove body, only remove if it's a custom container
      if (
        this.mountedComponent.target &&
        this.mountedComponent.target !== document.body &&
        this.mountedComponent.target.parentNode
      ) {
        this.mountedComponent.target.remove();
      }
      this.mountedComponent = null;
      log("MyAccountModal unmounted");
    }
  }

  /**
   * Handles account update events by making the appropriate API calls.
   * @param payload - The account update payload containing type and data.
   */
  private async handleAccountUpdate(payload: AccountUpdatePayload): Promise<void> {
    const { type, data } = payload;
    const baseApiUrl = this.experience?.config?.baseApiUrl || "https://layoutxmw-api.xmicrowave.com/api/v1";

    try {
      // Get authentication token
      const token = await this.getAuthToken();
      if (!token) {
        log("No authentication token available");
        this.emitUpdateComplete(type, false);
        return;
      }

      let response: Response;
      let transformedData: any = null; // Declare outside switch for use in userData refresh

      switch (type) {
        case "userData":
          // Update user data: PUT /api/v1/management/user
          // Get current user data and merge with updates
          const currentUser = this.experience?.dataVault?.user || {};
          log("Current user from DataVault:", currentUser);
          log("Update payload data:", data);
          
          // Merge: current user data first, then update data on top
          // This ensures we have all existing values, then override with new ones
          const mergedData = { ...currentUser, ...data };
          log("Merged data before transform:", mergedData);
          
          // Transform snake_case to camelCase
          transformedData = this.transformUserDataForAPI(mergedData);
          log("Final transformed data for API:", transformedData);
          
          // Verify we have required fields
          if (!transformedData.first_name || !transformedData.last_name || !transformedData.email) {
            log("ERROR: Missing required fields in transformed data!");
            console.error("Missing required fields:", {
              first_name: transformedData.first_name,
              last_name: transformedData.last_name,
              email: transformedData.email,
            });
            this.emitUpdateComplete(type, false);
            return;
          }
          
          const requestBody = JSON.stringify(transformedData);
          log("Request URL:", `${baseApiUrl}/management/user`);
          log("Request method: PUT");
          log("Request body (stringified):", requestBody);
          log("Request body (parsed):", JSON.parse(requestBody));
          
          response = await fetch(`${baseApiUrl}/management/user`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: requestBody,
          });
          
          log("Response status:", response.status, response.statusText);
          break;

        case "password":
          // Password reset: POST /api/v1/management/user/password/change
          // Sends a password reset email to the user
          log("Password reset URL:", `${baseApiUrl}/management/user/password/change`);
          log("Password reset method: POST");
          log("Password reset - sending request to trigger email");
          
          response = await fetch(`${baseApiUrl}/management/user/password/change`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            // Empty body or no body - the API will send reset email based on authenticated user
            body: JSON.stringify({}),
          });
          
          log("Password reset response status:", response.status, response.statusText);
          break;

        case "delete":
          // Delete user: DELETE /api/v1/management/user
          log("Delete account URL:", `${baseApiUrl}/management/user`);
          log("Delete account method: DELETE");
          log("User ID from data:", data.userId);
          
          response = await fetch(`${baseApiUrl}/management/user`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          log("Delete account response status:", response.status, response.statusText);
          break;

        default:
          log(`Unknown account update type: ${type}`);
          this.emitUpdateComplete(type, false);
          return;
      }

      // Handle 401 Unauthorized - try to refresh token and retry
      if (response.status === 401) {
        log("401 Unauthorized - Attempting token refresh and retry");
        const eventHub = this.experience?.eventHub;
        
        if (eventHub) {
          try {
            // Request token refresh via event
            const newToken = await new Promise<string | null>((resolve) => {
              const timeout = setTimeout(() => {
                log("Token refresh request timeout");
                eventHub.off("tokenRefreshResponse", tokenHandler);
                resolve(null);
              }, 10000); // 10 second timeout

              const tokenHandler = (token: string | null) => {
                clearTimeout(timeout);
                eventHub.off("tokenRefreshResponse", tokenHandler);
                resolve(token || null);
              };

              eventHub.once("tokenRefreshResponse", tokenHandler);
              eventHub.emit("tokenRefreshRequest");
            });
            
            if (newToken) {
              log("Token refreshed successfully, retrying account update request");
              
              // Retry the request with new token based on type
              let retryResponse: Response;
              
              switch (type) {
                case "userData":
                  retryResponse = await fetch(`${baseApiUrl}/management/user`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${newToken}`,
                    },
                    body: JSON.stringify(transformedData),
                  });
                  break;
                  
                case "password":
                  retryResponse = await fetch(`${baseApiUrl}/management/user/password/change`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${newToken}`,
                    },
                    body: JSON.stringify({}),
                  });
                  break;
                  
                case "delete":
                  retryResponse = await fetch(`${baseApiUrl}/management/user`, {
                    method: "DELETE",
                    headers: {
                      Authorization: `Bearer ${newToken}`,
                    },
                  });
                  break;
                  
                default:
                  this.emitUpdateComplete(type, false);
                  return;
              }
              
              if (retryResponse.ok) {
                log(`Account update successful after token refresh for type: ${type}`);
                
                // Parse response if available
                let responseData = null;
                try {
                  const responseText = await retryResponse.text();
                  if (responseText) {
                    try {
                      responseData = JSON.parse(responseText);
                      log("API response data:", responseData);
                    } catch (e) {
                      log("Response is not JSON:", responseText);
                    }
                  }
                } catch (e) {
                  log("No response body to parse");
                }
                
                this.emitUpdateComplete(type, true);
                
                // Handle post-update logic (same as original success path)
                if (type === "delete") {
                  log("Account deleted successfully, logging out user...");
                  try {
                    const loginManager = this.experience?.LoginManager;
                    if (loginManager && typeof loginManager.logout === 'function') {
                      loginManager.logout();
                    }
                    this.eventHub.emit("auth:guest");
                    this.eventHub.emit("sign_out", { event: "sign_out" });
                    log("User logged out after account deletion");
                  } catch (error) {
                    log("Error during logout after account deletion:", error);
                    this.eventHub.emit("auth:guest");
                    this.eventHub.emit("sign_out", { event: "sign_out" });
                  }
                }
                
                if (type === "userData" && responseData) {
                  // Refresh user data from the API to get the authoritative version
                  try {
                    log("Refreshing user data from API after update...");
                    if (this.experience?.dataVault?.fetchUserData) {
                      await this.experience.dataVault.fetchUserData();
                      log("User data refreshed successfully");
                    }
                  } catch (error) {
                    log("Error refreshing user data:", error);
                  }
                }
                
                return; // Success, exit early
              } else {
                log("Retry failed with status:", retryResponse.status);
                // If retry still fails with 401, session is truly expired
                if (retryResponse.status === 401) {
                  eventHub.emit("sessionExpired");
                }
                const retryErrorText = await retryResponse.text().catch(() => retryResponse.statusText);
                this.emitUpdateComplete(type, false);
                return;
              }
            } else {
              log("Token refresh failed, session expired");
              // Token refresh failed, session is truly expired
              eventHub.emit("sessionExpired");
              this.emitUpdateComplete(type, false);
              return;
            }
          } catch (refreshError) {
            log("Error during token refresh:", refreshError);
            // If refresh itself fails, treat as session expired
            if (eventHub) {
              eventHub.emit("sessionExpired");
            }
            this.emitUpdateComplete(type, false);
            return;
          }
        } else {
          // No eventHub available
          this.emitUpdateComplete(type, false);
          return;
        }
      }
      
      if (response.ok) {
        log(`Account update successful for type: ${type}`);
        
        // Try to parse response body for updated user data
        let responseData = null;
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              responseData = JSON.parse(responseText);
              log("API response data:", responseData);
            } catch (e) {
              log("Response is not JSON:", responseText);
            }
          }
        } catch (e) {
          // Response might not have body, that's okay
          log("No response body to parse");
        }
        
        this.emitUpdateComplete(type, true);

        // If account was deleted, handle logout/redirect
        if (type === "delete") {
          log("Account deleted successfully, logging out user...");
          try {
            // Get LoginManager from experience and logout
            const loginManager = this.experience?.LoginManager;
            if (loginManager && typeof loginManager.logout === 'function') {
              loginManager.logout();
            }
            // Emit guest event to update UI
            this.eventHub.emit("auth:guest");
            // Emit sign_out event
            this.eventHub.emit("sign_out", { event: "sign_out" });
            log("User logged out after account deletion");
          } catch (error) {
            log("Error during logout after account deletion:", error);
            // Still emit events as fallback
            this.eventHub.emit("auth:guest");
            this.eventHub.emit("sign_out", { event: "sign_out" });
          }
        }

        // If user data was updated, refresh user data from API and update DataVault
        if (type === "userData") {
          // Refresh user data from the API to get the authoritative version
          try {
            log("Refreshing user data from API after update...");
            if (this.experience?.dataVault?.fetchUserData) {
              await this.experience.dataVault.fetchUserData();
              log("User data refreshed successfully");
            } else {
              log("DataVault.fetchUserData not available, emitting response data if available");
              // Fallback: if we got response data, use it; otherwise use transformed data
              if (responseData && typeof responseData === 'object') {
                this.eventHub.emit("userData", responseData);
              } else {
                this.eventHub.emit("userData", transformedData);
              }
            }
          } catch (refreshError) {
            log("Error refreshing user data:", refreshError);
            // Still emit the data we sent as fallback
            if (responseData && typeof responseData === 'object') {
              this.eventHub.emit("userData", responseData);
            } else {
              this.eventHub.emit("userData", transformedData);
            }
          }
        }
      } else {
        const errorText = await response.text().catch(() => response.statusText);
        log(`Account update failed for type: ${type}`, {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        console.error(`Account update failed:`, {
          type,
          status: response.status,
          error: errorText,
        });
        this.emitUpdateComplete(type, false);
      }
    } catch (error: any) {
      log(`Error handling account update for type: ${type}`, error);
      this.emitUpdateComplete(type, false);
    }
  }

  /**
   * Transforms user data to snake_case format expected by the API.
   * Always includes all required fields - uses existing value or empty string.
   * The API may require all fields to be present to avoid deleting data.
   * @param data - The user data (may be in camelCase or snake_case format).
   * @returns The transformed data in snake_case format with all required fields.
   */
  private transformUserDataForAPI(data: any): any {
    // Always include all fields that the API expects in snake_case format
    // Use existing value (camelCase or snake_case) or empty string as fallback
    // This ensures we don't accidentally omit fields that the API might interpret as "delete"
    const transformed: any = {
      first_name: data.first_name ?? data.firstName ?? "",
      last_name: data.last_name ?? data.lastName ?? "",
      email: data.email ?? "",
      phone: data.phone ?? "",
      street: data.street ?? "",
      city: data.city ?? "",
      state: data.state ?? "",
      zip: data.zip ?? "",
      country: data.country ?? "",
      company: data.company ?? "",
    };
    
    log("Field transformation details:", {
      "first_name": { from: data.first_name ?? data.firstName, to: transformed.first_name },
      "last_name": { from: data.last_name ?? data.lastName, to: transformed.last_name },
      "email": { from: data.email, to: transformed.email },
      "phone": { from: data.phone, to: transformed.phone },
      "street": { from: data.street, to: transformed.street },
      "city": { from: data.city, to: transformed.city },
      "state": { from: data.state, to: transformed.state },
      "zip": { from: data.zip, to: transformed.zip },
      "country": { from: data.country, to: transformed.country },
      "company": { from: data.company, to: transformed.company },
    });
    
    return transformed;
  }

  /**
   * Normalizes RFQ data from API to match expected component structure.
   * @param rfq - Raw RFQ data from API (may have various field names).
   * @returns Normalized RFQ object with expected field names.
   */
  private normalizeRFQData(rfq: any): any {
    return {
      id: rfq.id ?? rfq.rfqId ?? rfq.rfq_id ?? 0,
      name: rfq.name ?? rfq.title ?? rfq.designName ?? rfq.design_name ?? 'Unnamed RFQ',
      desc: rfq.desc ?? rfq.description ?? rfq.desc ?? '',
      price: rfq.price ?? rfq.total ?? rfq.amount ?? rfq.cost ?? 0,
      date: rfq.date ?? rfq.createdAt ?? rfq.created_at ?? rfq.createdDate ?? rfq.created_date ?? new Date().toISOString().split('T')[0],
      status: rfq.status ?? rfq.state ?? 'Pending',
    };
  }

  /**
   * Fetches user RFQs from the API.
   * @returns Promise that resolves to an array of RFQ objects.
   */
  private async fetchUserRFQs(): Promise<any[]> {
    const baseApiUrl = this.experience?.config?.baseApiUrl || "https://layoutxmw-api.xmicrowave.com/api/v1";
    
    try {
      const token = await this.getAuthToken();
      if (!token) {
        log("No authentication token available for RFQ fetch");
        throw new Error("Authentication required to fetch RFQs");
      }

    log("Fetching user RFQs from API...");
    const response = await fetch(`${baseApiUrl}/rfq`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const rfqs = await response.json();
      log("RFQs fetched successfully:", rfqs);
      // Handle both array response and object with data property
      const rfqArray = Array.isArray(rfqs) ? rfqs : (rfqs?.data || rfqs?.rfqs || []);
      // Transform RFQ data to match expected structure
      const normalizedRFQs = Array.isArray(rfqArray) 
        ? rfqArray.map((rfq: any) => this.normalizeRFQData(rfq))
        : [];
      log("Normalized RFQs:", normalizedRFQs);
      return normalizedRFQs;
    } else {
      const errorText = await response.text().catch(() => response.statusText);
      log("Failed to fetch RFQs:", response.status, errorText);
      console.error("RFQ fetch failed:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      // Throw error so it can be caught by the event handler
      throw new Error(errorText || `Failed to fetch RFQs: ${response.status} ${response.statusText}`);
    }
    } catch (error: any) {
      log("Error fetching RFQs:", error);
      console.error("RFQ fetch error:", error);
      // Re-throw so the event handler can catch it
      throw error;
    }
  }

  /**
   * Gets the authentication token using the tokenRequest pattern.
   * @returns Promise that resolves to the token string or null.
   */
  private getAuthToken(): Promise<string | null> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        log("Token request timeout");
        this.eventHub.off("tokenResponse", tokenHandler);
        resolve(null);
      }, 10000); // 10 second timeout

      const tokenHandler = (token: string | null) => {
        clearTimeout(timeout);
        this.eventHub.off("tokenResponse", tokenHandler);
        resolve(token || null);
      };

      this.eventHub.once("tokenResponse", tokenHandler);
      this.eventHub.emit("tokenRequest");
    });
  }

  /**
   * Emits a completion event for the account update.
   * @param type - The type of update that was completed.
   * @param success - Whether the update was successful.
   */
  private emitUpdateComplete(type: string, success: boolean): void {
    this.eventHub.emit(`accountUpdate:${type}:complete`, success);
  }

  /**
   * Cleans up the manager by removing event listeners and unmounting the component.
   */
  cleanup(): void {
    log("Cleaning up MyAccountManager");

    // Unmount component if present
    if (this.mountedComponent) {
      this.unmountMyAccountModal();
    }

    // Remove event listeners
    this.eventHub.off("myInfo");
    this.eventHub.off("accountUpdate");
    this.eventHub.off("fetchUserRFQs");

    log("MyAccountManager cleanup complete");
  }
}

